import { useState } from "react";
import type { UserProfile } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCreateBlackout } from "@/hooks/useVisitAvailability";
import { formatMinuteOfDay } from "./visit-status";

const WHOLE_SCHOOL = "__school__";

const TIME_OPTIONS: number[] = [];
for (let minute = 420; minute <= 1200; minute += 30) TIME_OPTIONS.push(minute);

// Browser-local "YYYY-MM-DD" (en-CA formats as ISO). Staff and campus share a
// timezone in the pilot, so this matches the server's campus-local check.
function todayLocalDateStr(): string {
  return new Date().toLocaleDateString("en-CA");
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  advisors: UserProfile[];
};

export function AddBlackoutDialog({ open, onOpenChange, advisors }: Props): React.JSX.Element {
  const createBlackout = useCreateBlackout();

  const [who, setWho] = useState<string>(WHOLE_SCHOOL);
  const [isRange, setIsRange] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fullDay, setFullDay] = useState(true);
  const [startMinute, setStartMinute] = useState(600);
  const [endMinute, setEndMinute] = useState(840);
  const [reason, setReason] = useState("");

  const today = todayLocalDateStr();
  const effectiveDateTo = isRange ? dateTo : dateFrom;
  const datesInPast = effectiveDateTo !== "" && effectiveDateTo < today;
  const canSubmit =
    dateFrom !== "" &&
    effectiveDateTo !== "" &&
    dateFrom <= effectiveDateTo &&
    !datesInPast &&
    (fullDay || startMinute < endMinute);

  const submit = (): void => {
    createBlackout.mutate(
      {
        userId: who === WHOLE_SCHOOL ? null : who,
        dateFrom,
        dateTo: effectiveDateTo,
        startMinute: fullDay ? null : startMinute,
        endMinute: fullDay ? null : endMinute,
        ...(reason.trim() !== "" ? { reason: reason.trim() } : {}),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar excepción</DialogTitle>
          <DialogDescription>
            Bloquea la disponibilidad de un asesor o de toda la escuela en fechas
            específicas (vacaciones, festivos, salidas).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-ink-500">¿A quién aplica?</label>
            <Select value={who} onValueChange={setWho}>
              <SelectTrigger className="w-full">
                <SelectValue>
                  {who === WHOLE_SCHOOL
                    ? "Toda la escuela"
                    : advisors.find((a) => a.id === who)?.name ?? "Elegir"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={WHOLE_SCHOOL}>Toda la escuela</SelectItem>
                {advisors.map((advisor) => (
                  <SelectItem key={advisor.id} value={advisor.id}>
                    {advisor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-ink-500">Rango de fechas</label>
            <Switch checked={isRange} onCheckedChange={setIsRange} />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              min={today}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Fecha inicial"
              className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
            {isRange && (
              <>
                <span className="text-xs text-ink-400">a</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || today}
                  onChange={(e) => setDateTo(e.target.value)}
                  aria-label="Fecha final"
                  className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
                />
              </>
            )}
          </div>
          {datesInPast && (
            <p className="text-[12px] text-red-600">
              La excepción no puede terminar antes de hoy.
            </p>
          )}

          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-ink-500">Todo el día</label>
            <Switch checked={fullDay} onCheckedChange={setFullDay} />
          </div>
          {!fullDay && (
            <div className="flex items-center gap-2">
              <Select value={String(startMinute)} onValueChange={(v) => setStartMinute(Number(v))}>
                <SelectTrigger className="h-8 flex-1 text-[13px]">
                  <SelectValue>{formatMinuteOfDay(startMinute)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map((minute) => (
                    <SelectItem key={minute} value={String(minute)}>
                      {formatMinuteOfDay(minute)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-ink-400">a</span>
              <Select value={String(endMinute)} onValueChange={(v) => setEndMinute(Number(v))}>
                <SelectTrigger className="h-8 flex-1 text-[13px]">
                  <SelectValue>{formatMinuteOfDay(endMinute)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map((minute) => (
                    <SelectItem key={minute} value={String(minute)}>
                      {formatMinuteOfDay(minute)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={255}
            placeholder="Motivo (opcional)"
            className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-800 placeholder:text-ink-300 focus:border-moss focus:outline-none"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button size="sm" disabled={!canSubmit || createBlackout.isPending} onClick={submit}>
            {createBlackout.isPending ? "Guardando…" : "Agregar excepción"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
